<template>
  <div>
    <CModal
      color="primary"
      :show.sync="isShowModal"
      :closeOnBackdrop="false"
      size="lg"
    >
      <template #header>
        <h5 class="modal-title text-white">
          <i :class="activeTab === 'visibility' ? 'fa-solid fa-building-user mr-2' : activeTab === 'organisations' ? 'fa-solid fa-shield-halved mr-2' : 'fa-solid fa-file-shield mr-2'"></i>
          {{ activeTab === 'visibility' ? 'Primary Organisation Settings' : activeTab === 'organisations' ? 'Privacy Settings' : 'Privacy & Terms' }}
        </h5>
        <CButtonClose @click="close" />
      </template>

      <!-- Tab Navigation -->
      <div class="tab-nav-wrapper px-3 pt-3 pb-0">
        <ul class="settings-tab-list">
          <li
            class="settings-tab-item"
            :class="{ 'settings-tab-item--active': activeTab === 'visibility' }"
            @click="activeTab = 'visibility'"
          >
            <i class="fa-solid fa-eye mr-2"></i>
            Profile Visibility
          </li>
          <li
            class="settings-tab-item"
            :class="{ 'settings-tab-item--active': activeTab === 'organisations' }"
            @click="activeTab = 'organisations'"
          >
            <i class="fa-solid fa-building mr-2"></i>
            Your Organisations
          </li>
          <li
            class="settings-tab-item"
            :class="{ 'settings-tab-item--active': activeTab === 'terms' }"
            @click="activeTab = 'terms'"
          >
            <i class="fa-solid fa-file-shield mr-2"></i>
            Privacy &amp; Terms
          </li>
        </ul>
        <div class="tab-divider"></div>
      </div>

      <!-- TAB 1: Profile Visibility -->
      <div v-if="activeTab === 'visibility'" class="settings-body px-3 py-3">
        <p class="section-label mb-2">Who am I registered with?</p>
        <div class="d-flex align-items-center mb-4">
          <select class="form-control mr-3" v-model="selectedPrimaryOrgId" style="max-width:340px">
            <option
              v-for="org in organisations"
              :key="org.organisation_id || org.customer_uid"
              :value="org.organisation_id || org.customer_uid"
            >
              {{ org.organisation_name || org.customer_name }}{{ org.is_master ? ' (Primary)' : '' }}
            </option>
          </select>
          <CButton
            color="primary"
            @click="changePrimary"
            :disabled="changingPrimary"
            style="white-space:nowrap"
          >
            {{ changingPrimary ? 'Updating...' : 'Change Primary Organisation' }}
          </CButton>
        </div>

        <div class="info-box mb-4">
          <p class="info-title mb-1">What is my Primary Organisation?</p>
          <p class="info-text mb-0">
            Your primary organisation is simply the organisation you choose to be associated with on TalentFind.
          </p>
        </div>

        <p class="section-label mb-3">Your current registered organisations:</p>
        <div
          v-for="(org, index) in privacyList"
          :key="org.organisation_id || org.customer_uid"
          class="org-row d-flex align-items-center justify-content-between py-2"
        >
          <div class="d-flex align-items-center readonly-org-wrapper">
            <input
              type="checkbox"
              :id="`priv-${index}`"
              :checked="!org.is_private && !org.is_deleted"
              class="org-checkbox mr-3 readonly-checkbox"
            />
            <label :for="`priv-${index}`" class="org-label mb-0 d-flex align-items-center readonly-label" :class="{ 'org-label--disabled': org.is_deleted }">
              {{ org.organisation_name || org.customer_name }}
              <CBadge v-if="org.is_master && !org.is_deleted" color="primary" shape="pill" class="ml-2">Primary</CBadge>
              <CBadge v-if="org.is_deleted" color="danger" shape="pill" class="ml-2">Deactivated</CBadge>
            </label>
            <div v-if="!org.is_deleted" class="vis-tooltip">
              When selected, this organisation can view your profile for opportunities you are involved in with them.
            </div>
          </div>
          <span
            v-if="!org.is_deleted"
            class="remove-link"
            :class="{ 'remove-link--disabled': org.is_master }"
            :title="org.is_master ? 'Select a new Primary Organisation before removing this one' : ''"
            @click="!org.is_master && confirmRemove(org)"
          >
            Remove Registration
          </span>
          <span
            v-else
            class="reactivate-link"
            :class="{ 'reactivate-link--loading': reactivatingOrgId === (org.organisation_id || org.customer_uid) }"
            @click="reactivateOrg(org)"
          >
            {{ reactivatingOrgId === (org.organisation_id || org.customer_uid) ? 'Reactivating...' : 'Re-activate Registration' }}
          </span>
        </div>

        <p class="sub-label mt-3 mb-0">
          Manage visibility settings in the <strong>Your Organisations</strong> tab.
        </p>
      </div>

      <!-- TAB 2: Your Organisations (privacy radio) -->
      <div v-if="activeTab === 'organisations'" class="settings-body px-3 py-3">
        <p class="page-subtitle mb-4">
          Choose how your profile is shared with organisations. You can change this at any time.
        </p>

        <!-- Public -->
        <div
          class="privacy-card"
          :class="{ 'privacy-card--active': selectedVisibility === 'public' }"
          @click="selectedVisibility = 'public'"
        >
          <div class="d-flex align-items-start">
            <input
              type="radio"
              id="vis-public"
              v-model="selectedVisibility"
              value="public"
              class="privacy-radio mt-1 mr-3"
              @click.stop
            />
            <label for="vis-public" class="privacy-label mb-0">
              <span class="privacy-title">Public</span>
              <span class="privacy-desc">All organisations can view my full profile</span>
            </label>
          </div>
        </div>

        <!-- Selected Organisations Only -->
        <div
          class="privacy-card"
          :class="{ 'privacy-card--active': selectedVisibility === 'selected' }"
          @click="selectedVisibility = 'selected'"
        >
          <div class="d-flex align-items-start">
            <input
              type="radio"
              id="vis-selected"
              v-model="selectedVisibility"
              value="selected"
              class="privacy-radio mt-1 mr-3"
              @click.stop
            />
            <label for="vis-selected" class="privacy-label mb-0 w-100">
              <span class="privacy-title">
                Selected organisations only
                <span class="recommended-tag ml-2">Recommended</span>
              </span>
              <span class="privacy-desc">Only organisations I choose can view my full profile.</span>
            </label>
          </div>

          <!-- Org multi-select (only when this option is active) -->
          <div v-if="selectedVisibility === 'selected'" class="org-select-list mt-3" @click.stop>
            <p class="sub-label mb-2">Select organisations:</p>
            <div
              v-for="org in organisations"
              :key="org.organisation_id || org.customer_uid"
              class="org-check-row d-flex align-items-center py-1"
            >
              <input
                type="checkbox"
                :id="`sel-org-${org.organisation_id || org.customer_uid}`"
                :disabled="!!org.is_deleted"
                :checked="selectedOrgIds.includes(org.organisation_id || org.customer_uid)"
                @change="toggleSelectedOrg(org.organisation_id || org.customer_uid)"
                class="org-checkbox mr-2"
              />
              <label
                :for="`sel-org-${org.organisation_id || org.customer_uid}`"
                class="org-label mb-0 d-flex align-items-center"
                :class="{ 'org-label--disabled': org.is_deleted }"
              >
                {{ org.organisation_name || org.customer_name }}
                <CBadge v-if="org.is_deleted" color="danger" shape="pill" class="ml-2">Account Deactivated</CBadge>
              </label>
            </div>
          </div>
        </div>

        <!-- Limited (disabled) -->
        <div class="privacy-card privacy-card--disabled">
          <div class="d-flex align-items-start">
            <input
              type="radio"
              id="vis-limited"
              value="limited"
              class="privacy-radio mt-1 mr-3"
              disabled
            />
            <label for="vis-limited" class="privacy-label privacy-label--disabled mb-0">
              <span class="privacy-title">
                Limited profile
                <span class="limited-tag ml-2">anonymised</span>
              </span>
              <span class="privacy-desc">
                Organisations can view
                <span class="text-primary font-italic">some details such as role and specialty.</span>
              </span>
              <a href="#" class="privacy-link" @click.prevent>Preview what organisations see</a>
            </label>
          </div>
        </div>

        <!-- Private -->
        <div
          class="privacy-card"
          :class="{ 'privacy-card--active': selectedVisibility === 'private' }"
          @click="selectedVisibility = 'private'"
        >
          <div class="d-flex align-items-start">
            <input
              type="radio"
              id="vis-private"
              v-model="selectedVisibility"
              value="private"
              class="privacy-radio mt-1 mr-3"
              @click.stop
            />
            <label for="vis-private" class="privacy-label mb-0">
              <span class="privacy-title">Private</span>
              <span class="privacy-desc">My profile is not visible to organizations.</span>
              <a href="#" class="privacy-link" @click.prevent>
                I can still manage my career and access TalentFind Marketplace
              </a>
            </label>
          </div>
        </div>

        <p class="sub-label mt-3 mb-0">Your profile is always yours. You control who can see it.</p>
      </div>

      <!-- TAB 3: Privacy & Terms -->
      <div v-if="activeTab === 'terms'" class="settings-body px-3 py-3">
        <div v-if="termsError" class="alert alert-danger py-2 mb-3">{{ termsError }}</div>

        <div class="row">
          <!-- Left column -->
          <div class="col-md-6">

            <!-- Subscription Preferences -->
            <div class="terms-group mb-3">
              <p class="section-label mb-2">
                Subscription Preferences <span class="text-danger">*</span>
              </p>
              <div class="d-flex align-items-start mb-3">
                <input
                  type="checkbox"
                  id="terms-sub-pref"
                  :checked="privacyTerms.subscription_preferences"
                  @change="handleTermsCheck('subscription_preferences', $event)"
                  class="org-checkbox mr-2 mt-1"
                />
                <label for="terms-sub-pref" class="sub-label mb-0" style="cursor:pointer">
                  Yes, you may send me email, Whatsapp and SMS messages with the following content:
                </label>
              </div>

              <div class="terms-toggles">
                <div class="terms-toggle-row">
                  <span>New Job Opportunities</span>
                  <CSwitch color="success" shape="pill" variant="3d"
                    :checked="privacyTerms.new_job_opportunities"
                    :disabled="!privacyTerms.subscription_preferences"
                    @change.native="handleTermsCheck('new_job_opportunities', $event)"
                  />
                </div>
                <div class="terms-toggle-row">
                  <span>Urgent Messages and Alerts</span>
                  <CSwitch color="success" shape="pill" variant="3d"
                    :checked="privacyTerms.urgent_messages_and_alerts"
                    :disabled="!privacyTerms.subscription_preferences"
                    @change.native="handleTermsCheck('urgent_messages_and_alerts', $event)"
                  />
                </div>
                <div class="terms-toggle-row">
                  <span>Invitations relating to Jobs</span>
                  <CSwitch color="success" shape="pill" variant="3d"
                    :checked="privacyTerms.invitations_relating_to_jobs"
                    :disabled="!privacyTerms.subscription_preferences"
                    @change.native="handleTermsCheck('invitations_relating_to_jobs', $event)"
                  />
                </div>
                <div class="terms-toggle-row">
                  <span>Promotions &amp; Press Releases</span>
                  <CSwitch color="success" shape="pill" variant="3d"
                    :checked="privacyTerms.promotions_and_press_releases"
                    :disabled="!privacyTerms.subscription_preferences"
                    @change.native="handleTermsCheck('promotions_and_press_releases', $event)"
                  />
                </div>
              </div>
            </div>

            <!-- Applied to other agencies -->
            <div class="terms-group mb-3">
              <p class="section-label mb-2">Applied to other agencies</p>
              <div class="d-flex align-items-center">
                <label class="terms-radio-label mr-4">
                  <input type="radio" :value="true" v-model="privacyTerms.applied_to_other_agencies" class="mr-1" />
                  Yes
                </label>
                <label class="terms-radio-label">
                  <input type="radio" :value="false" v-model="privacyTerms.applied_to_other_agencies" class="mr-1" />
                  No
                </label>
              </div>
            </div>

            <!-- UnSubscribe -->
            <div class="terms-group mb-3">
              <div class="d-flex align-items-center" style="gap:16px">
                <span class="section-label mb-0">UnSubscribe</span>
                <CSwitch color="success" shape="pill" variant="3d"
                  :checked="privacyTerms.subscribed"
                  @change.native="handleTermsCheck('subscribed', $event)"
                />
              </div>
            </div>

            <!-- Privacy Statement -->
            <a href="javascript:void(0)" class="privacy-link d-inline-block mt-1" @click="showTermsPopup">
              <i class="fa-solid fa-file-lines mr-1"></i>Privacy Statement
            </a>
          </div>

          <!-- Right column -->
          <div class="col-md-6">

            <!-- How did you hear about us -->
            <div class="terms-group mb-3">
              <p class="section-label mb-2">How did you hear about us?</p>
              <select class="form-control" v-model="privacyTerms.display_data_id">
                <option :value="null">-- Select --</option>
                <option v-for="opt in displayDataOptions" :key="opt.code" :value="opt.code">
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <!-- How did we do (star rating) -->
            <div class="terms-group mb-3">
              <p class="section-label mb-2">How did we do?</p>
              <div class="star-rating">
                <i
                  v-for="n in 5"
                  :key="n"
                  :class="n <= privacyTerms.our_rating ? 'fa-solid fa-star' : 'fa-regular fa-star'"
                  class="star-icon"
                  @click="privacyTerms.our_rating = n"
                ></i>
              </div>
            </div>

            <!-- What could we do better -->
            <div class="terms-group mb-3">
              <p class="section-label mb-2">What could we do better?</p>
              <textarea
                class="form-control"
                rows="3"
                v-model="privacyTerms.do_better"
                maxlength="225"
                placeholder="Share your feedback..."
              ></textarea>
            </div>

            <!-- Delete My Account -->
            <div class="terms-group">
              <p class="section-label mb-2">Delete My Account</p>
              <div class="d-flex align-items-center">
                <label class="terms-radio-label mr-4">
                  <input type="radio" :value="true" v-model="privacyTerms.delete_my_account" class="mr-1" /> Yes
                </label>
                <label class="terms-radio-label">
                  <input type="radio" :value="false" v-model="privacyTerms.delete_my_account" class="mr-1" /> No
                </label>
              </div>
            </div>

          </div>
        </div>
      </div>

      <template #footer>
        <CButton color="secondary" variant="outline" @click="close">Cancel</CButton>
        <CButton
          v-if="activeTab === 'organisations'"
          color="primary"
          @click="saveOrgVisibility"
          :disabled="savingVisibility || !selectedVisibility"
        >
          {{ savingVisibility ? 'Saving...' : 'Save' }}
        </CButton>
        <CButton
          v-if="activeTab === 'terms'"
          color="primary"
          @click="savePrivacyTerms"
          :disabled="savingPrivacyTerms || !privacyTerms.subscription_preferences"
        >
          {{ savingPrivacyTerms ? 'Saving...' : 'Save' }}
        </CButton>
      </template>
    </CModal>

    <!-- Privacy Statement PDF modal -->
    <PreviewModal
      :modalTitle="previewModal.modalTitle"
      :modalColor="previewModal.modalColor"
      :isShowPopup="previewModal.isShowPopup"
      :buttons="previewModal.buttons"
      :modalCallBack="termsModalCallBack"
      :isPreview="previewModal.isPreview"
      :iframe="iframe"
      :size="previewModal.size"
      :closeOnBackdrop="previewModal.closeOnBackdrop"
    />

    <!-- Reactivate Registration confirmation -->
    <CModal
      color="success"
      :show.sync="showReactivateConfirm"
      :closeOnBackdrop="false"
      size="sm"
    >
      <template #header>
        <h5 class="modal-title text-white">Re-activate Registration</h5>
        <CButtonClose @click="showReactivateConfirm = false" />
      </template>
      <div class="settings-body px-2 py-2">
        <p class="org-label mb-0">
          Re-activate your registration with
          <strong>{{ reactivateTarget && (reactivateTarget.organisation_name || reactivateTarget.customer_name) }}</strong>?
          You will regain access to this organisation on TalentFind.
        </p>
      </div>
      <template #footer>
        <CButton color="secondary" variant="outline" @click="showReactivateConfirm = false">Cancel</CButton>
        <CButton color="success" @click="doReactivate" :disabled="reactivatingOrgId !== null">
          {{ reactivatingOrgId !== null ? 'Reactivating...' : 'Confirm' }}
        </CButton>
      </template>
    </CModal>

    <!-- Remove Registration confirmation -->
    <CModal
      color="primary"
      :show.sync="showRemoveConfirm"
      :closeOnBackdrop="false"
      size="sm"
    >
      <template #header>
        <h5 class="modal-title text-white">Remove Registration</h5>
        <CButtonClose @click="showRemoveConfirm = false" />
      </template>
      <div class="settings-body px-2 py-2">
        <p class="org-label mb-0">
          You will no longer be registered with
          <strong>{{ removeTarget && (removeTarget.organisation_name || removeTarget.customer_name) }}</strong>.
          Your TalentFind profile and history will be retained.
        </p>
      </div>
      <template #footer>
        <CButton color="secondary" variant="outline" @click="showRemoveConfirm = false">Cancel</CButton>
        <CButton color="primary" @click="doRemove" :disabled="removing">
          {{ removing ? 'Removing...' : 'Confirm' }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import PreviewModal from '@/components/reusable/PreviewModal';
import pdf from 'vue-pdf';

export default {
  name: 'PrimaryOrgSettingsModal',
  components: { PreviewModal },
  props: {
    isShowPopup: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: 'visibility',
      isShowModal: this.isShowPopup,
      selectedPrimaryOrgId: null,
      privacyList: [],
      originalPrivacyList: [],
      // Your Organisations tab
      selectedVisibility: null,
      selectedOrgIds: [],
      // Privacy & Terms tab
      privacyTerms: {
        customer_uid: null,
        candidate_uid: null,
        subscription_preferences: false,
        new_job_opportunities: false,
        urgent_messages_and_alerts: false,
        invitations_relating_to_jobs: false,
        promotions_and_press_releases: false,
        applied_to_other_agencies: null,
        display_data_id: null,
        our_rating: 0,
        do_better: '',
        subscribed: false,
        marketing: null,
        delete_my_account: false,
        privacy_acceptance: null,
      },
      termsError: '',
      savingPrivacyTerms: false,
      previewModal: {
        modalColor: 'primary',
        modalTitle: 'Terms and Privacy Policy',
        isShowPopup: false,
        buttons: ['Close'],
        isPreview: false,
        size: 'lg',
        closeOnBackdrop: false,
      },
      iframe: {
        src: '',
        loaded: false,
        isImg: false,
        pdf: {
          ispdf: true,
          src: '',
          numPages: undefined,
          addContentClasses: '',
        },
      },
      // UI state
      showRemoveConfirm: false,
      removeTarget: null,
      changingPrimary: false,
      saving: false,
      savingVisibility: false,
      removing: false,
      reactivatingOrgId: null,
      showReactivateConfirm: false,
      reactivateTarget: null,
    };
  },
  computed: {
    ...mapGetters(['getCandidateOrganisation', 'candidateProfile', 'getDisplayData']),
    organisations() {
      return this.getCandidateOrganisation;
    },
    displayDataOptions() {
      return this.getDisplayData || [];
    },
    hasPrivacyChanged() {
      return this.privacyList.some((item, index) => {
        const original = this.originalPrivacyList[index];
        return original && !!item.is_private !== !!original.is_private;
      });
    },
  },
  watch: {
    isShowPopup(val) {
      this.isShowModal = val;
      if (val) this.init();
    },
    isShowModal(val) {
      if (!val) this.$emit('close');
    },
  },
  mounted() {
    if (this.isShowPopup) this.init();
  },
  methods: {
    ...mapActions([
      'fetchCandidateOrganisation',
      'changePrimaryOrganisation',
      'updateOrganisationPrivacy',
      'removeOrganisationRegistration',
      'reactivateOrganisationRegistration',
      'showToast',
      'updateCandidate',
      'fetchUser',
      'displayData',
    ]),
    init() {
      this.activeTab = 'visibility';
      this.displayData();
      this.fetchCandidateOrganisation().then(() => {
        this.buildPrivacyList();
        this.buildOrgVisibility();
      });
      this.buildPrivacyTerms();
    },
    buildPrivacyList() {
      const activeOrgs = this.organisations.filter(o => !o.is_deleted);
      const primary = activeOrgs.find(o => o.is_master);
      this.selectedPrimaryOrgId = primary
        ? (primary.organisation_id || primary.customer_uid)
        : null;
      this.privacyList = this.organisations.map(o => ({ ...o, is_private: !!o.is_private }));
      this.originalPrivacyList = this.organisations.map(o => ({ ...o, is_private: !!o.is_private }));
    },
    buildOrgVisibility() {
      const orgs = this.organisations;
      if (!orgs.length) {
        this.selectedVisibility = 'public';
        this.selectedOrgIds = [];
        return;
      }
      const allPublic = orgs.every(o => !o.is_private);
      const allPrivate = orgs.every(o => !!o.is_private);
      if (allPublic) {
        this.selectedVisibility = 'public';
        this.selectedOrgIds = [];
      } else if (allPrivate) {
        this.selectedVisibility = 'private';
        this.selectedOrgIds = [];
      } else {
        this.selectedVisibility = 'selected';
        this.selectedOrgIds = orgs
          .filter(o => !o.is_private)
          .map(o => o.organisation_id || o.customer_uid);
      }
    },
    close() {
      this.isShowModal = false;
    },
    togglePrivacy(index) {
      const item = this.privacyList[index];
      this.$set(this.privacyList, index, { ...item, is_private: !item.is_private });
    },
    toggleSelectedOrg(orgId) {
      const idx = this.selectedOrgIds.indexOf(orgId);
      if (idx === -1) {
        this.selectedOrgIds.push(orgId);
      } else {
        this.selectedOrgIds.splice(idx, 1);
      }
    },
    async changePrimary() {
      const org = this.organisations.find(
        o => (o.organisation_id || o.customer_uid) === this.selectedPrimaryOrgId
      );
      if (!org) return;
      const candidateUid = this.candidateProfile && this.candidateProfile.candidate_uid;
      this.changingPrimary = true;
      try {
        await this.changePrimaryOrganisation({
          candidate_uid: candidateUid,
          organisation_id: org.organisation_id,
        });
        await this.fetchCandidateOrganisation();
        this.buildPrivacyList();
        this.buildOrgVisibility();
        this.showToast({ class: 'bg-success text-white', message: 'Primary organisation updated successfully' });
      } catch (e) {
        this.showToast({ class: 'bg-danger text-white', message: 'Failed to update primary organisation' });
      } finally {
        this.changingPrimary = false;
      }
    },
    async savePrivacy() {
      const candidateUid = this.candidateProfile && this.candidateProfile.candidate_uid;
      const organisations = this.privacyList.map(o => ({
        organisation_id: o.organisation_id,
        is_private: !!o.is_private,
      }));
      this.saving = true;
      try {
        await this.updateOrganisationPrivacy({ candidate_uid: candidateUid, organisations });
        await this.fetchCandidateOrganisation();
        this.buildPrivacyList();
        this.buildOrgVisibility();
        this.showToast({ class: 'bg-success text-white', message: 'Privacy settings saved successfully' });
      } catch (e) {
        this.showToast({ class: 'bg-danger text-white', message: 'Failed to save privacy settings' });
      } finally {
        this.saving = false;
      }
    },
    async saveOrgVisibility() {
      const profile = this.candidateProfile;
      const candidateUid = profile && profile.candidate_uid;
      const customerUid = profile && profile.customer_uid;
      let organisations;
      let candidateIsPrivate = null; // only set for public/private; not touched for selected

      if (this.selectedVisibility === 'public') {
        organisations = this.organisations.map(o => ({
          organisation_id: o.organisation_id,
          is_private: false,
        }));
        candidateIsPrivate = false;
      } else if (this.selectedVisibility === 'selected') {
        organisations = this.organisations.map(o => {
          const id = o.organisation_id || o.customer_uid;
          return {
            organisation_id: o.organisation_id,
            is_private: !this.selectedOrgIds.includes(id),
          };
        });
      } else if (this.selectedVisibility === 'private') {
        organisations = this.organisations.map(o => ({
          organisation_id: o.organisation_id,
          is_private: true,
        }));
        candidateIsPrivate = true;
      } else {
        return;
      }

      this.savingVisibility = true;
      try {
        await this.updateOrganisationPrivacy({ candidate_uid: candidateUid, organisations });
        if (candidateIsPrivate !== null) {
          await this.updateCandidate({
            data: { candidate_uid: candidateUid, customer_uid: customerUid, is_private: candidateIsPrivate },
            customer_uid: customerUid,
          });
          await this.fetchUser();
        }
        await this.fetchCandidateOrganisation();
        this.buildPrivacyList();
        this.buildOrgVisibility();
        this.showToast({ class: 'bg-success text-white', message: 'Privacy settings saved successfully' });
      } catch (e) {
        this.showToast({ class: 'bg-danger text-white', message: 'Failed to save privacy settings' });
      } finally {
        this.savingVisibility = false;
      }
    },
    // ── Privacy & Terms ──────────────────────────────
    buildPrivacyTerms() {
      const p = this.candidateProfile;
      if (!p || !Object.keys(p).length) return;
      this.privacyTerms = {
        customer_uid: p.customer_uid,
        candidate_uid: p.candidate_uid,
        subscription_preferences: p.subscription_preferences || false,
        new_job_opportunities: p.new_job_opportunities || false,
        urgent_messages_and_alerts: p.urgent_messages_and_alerts || false,
        invitations_relating_to_jobs: p.invitations_relating_to_jobs || false,
        promotions_and_press_releases: p.promotions_and_press_releases || false,
        applied_to_other_agencies: p.applied_to_other_agencies !== undefined ? p.applied_to_other_agencies : null,
        // store as plain code to bind to native <select>
        display_data_id: p.display_data_id != null && p.display_data ? p.display_data.display_data_id : null,
        our_rating: p.our_rating || 0,
        do_better: p.do_better || '',
        subscribed: p.subscribed || false,
        marketing: p.marketing || null,
        delete_my_account: p.delete_my_account || false,
        privacy_acceptance: p.privacy_acceptance || null,
      };
    },
    handleTermsCheck(name, event) {
      const checked = event.target.checked;
      this.$set(this.privacyTerms, name, checked);
      if (name === 'subscription_preferences') {
        // mirror exact logic from existing component: cascade to all toggles
        this.privacyTerms.new_job_opportunities = checked;
        this.privacyTerms.urgent_messages_and_alerts = checked;
        this.privacyTerms.invitations_relating_to_jobs = checked;
        this.privacyTerms.promotions_and_press_releases = checked;
      } else {
        // if all 4 toggles are off, uncheck the master
        const { new_job_opportunities, urgent_messages_and_alerts, invitations_relating_to_jobs, promotions_and_press_releases } = this.privacyTerms;
        if (!new_job_opportunities && !urgent_messages_and_alerts && !invitations_relating_to_jobs && !promotions_and_press_releases) {
          this.privacyTerms.subscription_preferences = false;
        }
      }
    },
    async savePrivacyTerms() {
      this.termsError = '';
      if (!this.privacyTerms.subscription_preferences) {
        this.termsError = 'Please fill Subscription Preferences';
        return;
      }
      const payload = {
        customer_uid: this.privacyTerms.customer_uid,
        candidate_uid: this.privacyTerms.candidate_uid,
        subscription_preferences: this.privacyTerms.subscription_preferences,
        new_job_opportunities: this.privacyTerms.new_job_opportunities,
        urgent_messages_and_alerts: this.privacyTerms.urgent_messages_and_alerts,
        invitations_relating_to_jobs: this.privacyTerms.invitations_relating_to_jobs,
        promotions_and_press_releases: this.privacyTerms.promotions_and_press_releases,
        applied_to_other_agencies: this.privacyTerms.applied_to_other_agencies,
        display_data_id: this.privacyTerms.display_data_id,
        our_rating: this.privacyTerms.our_rating || null,
        do_better: this.privacyTerms.do_better || null,
        subscribed: this.privacyTerms.subscribed,
        marketing: this.privacyTerms.marketing,
        delete_my_account: this.privacyTerms.delete_my_account,
        privacy_acceptance: this.privacyTerms.privacy_acceptance,
      };
      this.savingPrivacyTerms = true;
      try {
        await this.updateCandidate({ data: payload, customer_uid: payload.customer_uid });
        await this.fetchUser();
        this.buildPrivacyTerms();
        this.showToast({ class: 'bg-success text-white', message: 'Privacy & Terms saved successfully' });
      } catch (e) {
        this.showToast({ class: 'bg-danger text-white', message: 'Failed to save Privacy & Terms' });
      } finally {
        this.savingPrivacyTerms = false;
      }
    },
    showTermsPopup() {
      const data = { document_ext: 'PDF', link: '/doc/Talentfind_privacy_policy.pdf' };
      this.iframe.isImg = false;
      this.iframe.loaded = false;
      this.iframe.pdf.ispdf = true;
      this.iframe.pdf.src = pdf.createLoadingTask(data.link);
      setTimeout(() => {
        this.iframe.pdf.src.promise.then(p => {
          this.iframe.pdf.numPages = p.numPages;
        });
      }, 100);
      this.previewModal.isPreview = true;
      this.previewModal.isShowPopup = true;
    },
    termsModalCallBack() {
      this.previewModal.isPreview = false;
      this.previewModal.isShowPopup = false;
    },
    reactivateOrg(org) {
      this.reactivateTarget = { ...org };
      this.showReactivateConfirm = true;
    },
    async doReactivate() {
      if (!this.reactivateTarget) return;
      const candidateUid = this.candidateProfile && this.candidateProfile.candidate_uid;
      const orgId = this.reactivateTarget.organisation_id || this.reactivateTarget.customer_uid;
      this.reactivatingOrgId = orgId;
      try {
        await this.reactivateOrganisationRegistration({
          candidate_uid: candidateUid,
          organisation_id: this.reactivateTarget.organisation_id,
        });
        this.showReactivateConfirm = false;
        this.reactivateTarget = null;
        await this.fetchCandidateOrganisation();
        this.buildPrivacyList();
        this.buildOrgVisibility();
        this.showToast({ class: 'bg-success text-white', message: 'Organisation reactivated successfully' });
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Failed to reactivate organisation';
        this.showToast({ class: 'bg-danger text-white', message: msg });
      } finally {
        this.reactivatingOrgId = null;
      }
    },
    confirmRemove(org) {
      this.removeTarget = { ...org };
      this.showRemoveConfirm = true;
    },
    async doRemove() {
      if (!this.removeTarget) return;
      const candidateUid = this.candidateProfile && this.candidateProfile.candidate_uid;
      this.removing = true;
      try {
        await this.removeOrganisationRegistration({
          candidate_uid: candidateUid,
          organisation_id: this.removeTarget.organisation_id,
        });
        this.showRemoveConfirm = false;
        this.removeTarget = null;
        await this.fetchCandidateOrganisation();
        this.buildPrivacyList();
        this.buildOrgVisibility();
        this.showToast({ class: 'bg-success text-white', message: 'Registration removed successfully' });
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.detail
          ? e.response.data.detail
          : 'Failed to remove registration';
        this.showToast({ class: 'bg-danger text-white', message: msg });
      } finally {
        this.removing = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── Tab Navigation ─────────────────────────────── */
.tab-nav-wrapper {
  background: #fff;
  border-bottom: 1px solid #e4e7ec;
}

.settings-tab-list {
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  gap: 4px;
}

.settings-tab-item {
  padding: 9px 20px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6c757d;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  border: 1px solid transparent;
  border-bottom: none;
  transition: color 0.15s, background 0.15s;
  user-select: none;
  position: relative;
  bottom: -1px;
}

.settings-tab-item:hover {
  color: #321fdb;
  background: #f4f3ff;
}

.settings-tab-item--active {
  color: #321fdb;
  background: #fff;
  border-color: #e4e7ec;
  font-weight: 600;
}

.tab-divider {
  height: 0;
}

/* ── Common body ─────────────────────────────────── */
.settings-body {
  color: #2c3e50;
  min-height: 320px;
}

.section-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: #2c3e50;
}

.sub-label {
  font-size: 0.82rem;
  color: #6c757d;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #6c757d;
}

.info-box {
  background: #f8f9ff;
  border-left: 3px solid #321fdb;
  border-radius: 4px;
  padding: 10px 14px;
}

.info-title {
  font-weight: 700;
  font-size: 0.875rem;
  color: #2c3e50;
}

.info-text {
  font-size: 0.875rem;
  color: #2c3e50;
}

/* ── Org rows (Profile Visibility tab) ───────────── */
.org-row {
  border-bottom: 1px solid #f0f0f0;
}

.org-checkbox {
  width: 17px;
  height: 17px;
  cursor: pointer;
  accent-color: #321fdb;
  flex-shrink: 0;
}

.org-label {
  font-size: 0.9rem;
  color: #2c3e50;
  cursor: pointer;
}

.remove-link {
  font-size: 0.82rem;
  color: #e55353;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}

.remove-link--disabled {
  color: #adb5bd;
  cursor: not-allowed;
}

.reactivate-link {
  font-size: 0.82rem;
  color: #2eb85c;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
}

.reactivate-link--loading {
  color: #adb5bd;
  cursor: not-allowed;
}

/* ── Read-only checkbox + tooltip ────────────────── */
.readonly-checkbox {
  pointer-events: none;
}

.readonly-label {
  cursor: default;
}

.readonly-org-wrapper {
  position: relative;
}

.readonly-org-wrapper:hover .vis-tooltip {
  display: block;
}

.vis-tooltip {
  display: none;
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  background: #2c3e50;
  color: #fff;
  font-size: 0.8rem;
  line-height: 1.4;
  padding: 7px 11px;
  border-radius: 6px;
  width: 300px;
  z-index: 1100;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  pointer-events: none;
}

.vis-tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 16px;
  border: 6px solid transparent;
  border-top-color: #2c3e50;
}

/* ── Privacy cards (Your Organisations tab) ──────── */
.privacy-card {
  border: 1.5px solid #e4e7ec;
  border-radius: 8px;
  padding: 14px 16px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  background: #fff;
}

.privacy-card:hover:not(.privacy-card--disabled) {
  border-color: #6b6bdf;
  background: #fafaff;
}

.privacy-card--active {
  border-color: #321fdb;
  background: #f4f3ff;
}

.privacy-card--disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #f9f9f9;
}

.privacy-radio {
  width: 17px;
  height: 17px;
  accent-color: #321fdb;
  flex-shrink: 0;
  cursor: pointer;
}

.privacy-label {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.privacy-label--disabled {
  cursor: not-allowed;
}

.privacy-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
}

.privacy-desc {
  font-size: 0.82rem;
  color: #6c757d;
}

.privacy-link {
  font-size: 0.82rem;
  color: #321fdb;
  text-decoration: underline;
}

.recommended-tag {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  background: #321fdb;
  color: #fff;
  border-radius: 30px;
  padding: 1px 9px;
  vertical-align: middle;
  letter-spacing: 0.02em;
}

.limited-tag {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 600;
  background: #768192;
  color: #fff;
  border-radius: 30px;
  padding: 1px 9px;
  vertical-align: middle;
  letter-spacing: 0.02em;
}

/* ── Org select list inside "Selected" card ──────── */
.org-select-list {
  border-top: 1px solid #e4e7ec;
  padding-top: 10px;
}

.org-check-row {
  gap: 8px;
}

.org-label--disabled {
  color: #adb5bd;
  cursor: not-allowed;
}


/* ── Privacy & Terms tab ─────────────────────────── */
.terms-group {
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 12px;
}

.terms-group:last-child {
  border-bottom: none;
}

.terms-toggles {
  border: 1px solid #e4e7ec;
  border-radius: 6px;
  overflow: hidden;
}

.terms-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.875rem;
  color: #2c3e50;
}

.terms-toggle-row:last-child {
  border-bottom: none;
}

.terms-radio-label {
  font-size: 0.875rem;
  color: #2c3e50;
  cursor: pointer;
  margin-bottom: 0;
}


.star-rating {
  display: flex;
  gap: 5px;
}

.star-icon {
  font-size: 1.4rem;
  cursor: pointer;
  color: #f6a623;
  transition: transform 0.1s;
}

.star-icon:hover {
  transform: scale(1.15);
}
</style>
